<template>
  <div>
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-primary-600 to-primary-800 text-white section-padding">
      <div class="container-custom text-center">
        <h1 class="text-4xl md:text-5xl font-bold mb-6">
          Our Work Gallery
        </h1>
        <p class="text-xl text-primary-100 max-w-3xl mx-auto">
          Browse through our portfolio of completed projects. From simple repairs to major renovations, 
          see the quality and craftsmanship that sets CW Home Improvements apart.
        </p>
      </div>
    </section>

    <!-- Gallery Filters -->
    <section class="section-padding bg-white">
      <div class="container-custom">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-gray-900 mb-4">Project Categories</h2>
          <p class="text-xl text-gray-600">
            Filter by project type to see specific examples of our work.
          </p>
        </div>

        <!-- Filter Buttons -->
        <div class="flex flex-wrap justify-center gap-4 mb-12">
          <button 
            @click="activeFilter = 'all'"
            :class="[
              'px-6 py-3 rounded-lg font-medium transition-colors',
              activeFilter === 'all' 
                ? 'bg-primary-800 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]"
          >
            All Projects
          </button>
          <button 
            @click="activeFilter = 'flooring'"
            :class="[
              'px-6 py-3 rounded-lg font-medium transition-colors',
              activeFilter === 'flooring' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]"
          >
            Flooring
          </button>
          <button 
            @click="activeFilter = 'drywall'"
            :class="[
              'px-6 py-3 rounded-lg font-medium transition-colors',
              activeFilter === 'drywall' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]"
          >
            Drywall Repair
          </button>
          <button 
            @click="activeFilter = 'trimming'"
            :class="[
              'px-6 py-3 rounded-lg font-medium transition-colors',
              activeFilter === 'trimming' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]"
          >
            Trimming & Molding
          </button>
          <button 
            @click="activeFilter = 'painting'"
            :class="[
              'px-6 py-3 rounded-lg font-medium transition-colors',
              activeFilter === 'painting' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]"
          >
            Painting
          </button>
          <button 
            @click="activeFilter = 'remodeling'"
            :class="[
              'px-6 py-3 rounded-lg font-medium transition-colors',
              activeFilter === 'remodeling' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]"
          >
            Home Remodeling
          </button>
        </div>

        <!-- Gallery Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div 
            v-for="project in filteredProjects" 
            :key="project.id"
            class="card group cursor-pointer"
            @click="openModal(project)"
          >
            <div class="relative overflow-hidden rounded-lg mb-4">
              <div class="bg-gray-200 h-64 overflow-hidden">
                <img 
                  v-if="project.images && project.images.length > 0"
                  :src="project.images[0].url" 
                  :alt="project.images[0].title"
                  class="w-full h-full object-cover"
                  @error="handleImageError"
                />
                <div v-else class="h-64 flex items-center justify-center">
                  <div class="text-center">
                    <svg class="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z"></path>
                    </svg>
                    <p class="text-gray-600">Project Image</p>
                  </div>
                </div>
              </div>
              <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center">
                <div class="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path>
                  </svg>
                </div>
              </div>
              <div class="absolute top-4 left-4">
                <span :class="[
                  'px-3 py-1 rounded-full text-sm font-medium',
                  getCategoryColor(project.category)
                ]">
                  {{ project.category }}
                </span>
              </div>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ project.title }}</h3>
            <p class="text-gray-600 mb-4">{{ project.description }}</p>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-500">{{ project.location }}</span>
              <span class="text-sm text-gray-500">{{ project.date }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Project Statistics -->
    <section class="section-padding gradient-bg">
      <div class="container-custom">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Our Project Statistics
          </h2>
          <p class="text-xl text-gray-600">
            Numbers that speak to our experience and success in delivering quality work.
          </p>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div class="text-center">
            <div class="text-4xl md:text-5xl font-bold text-primary-800 mb-2">100+</div>
            <div class="text-gray-700 font-medium">Projects Completed</div>
          </div>
          <div class="text-center">
            <div class="text-4xl md:text-5xl font-bold text-primary-800 mb-2">100+</div>
            <div class="text-gray-700 font-medium">Happy Customers</div>
          </div>
          <div class="text-center">
            <div class="text-4xl md:text-5xl font-bold text-primary-800 mb-2">5+</div>
            <div class="text-gray-700 font-medium">Years Experience</div>
          </div>
          <div class="text-center">
            <div class="text-4xl md:text-5xl font-bold text-primary-800 mb-2">100%</div>
            <div class="text-gray-700 font-medium">Satisfaction Rate</div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section-padding bg-primary-800 text-white">
      <div class="container-custom text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-6">
          Ready to Start Your Project?
        </h2>
        <p class="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
          Let us help you bring your vision to life. Contact us today for a free consultation and estimate.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
                      <a href="tel:5613983356" class="btn-secondary bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600">
            Call Now: (561) 398-3356
          </a>
        </div>
      </div>
    </section>

    <!-- Project Modal -->
    <div v-if="selectedProject" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click="closeModal">
      <div class="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" @click.stop>
        <div class="p-6">
          <div class="flex justify-between items-start mb-4">
            <h3 class="text-2xl font-bold text-gray-900">{{ selectedProject.title }}</h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <div class="relative mb-6">
            <div class="bg-gray-200 rounded-lg h-80 overflow-hidden">
              <div class="flex transition-transform duration-300 ease-in-out" :style="{ transform: `translateX(-${currentImageIndex * 100}%)` }">
                <div v-for="(image, index) in selectedProject.images" :key="index" class="w-full flex-shrink-0 h-80">
                  <img 
                    :src="image.url" 
                    :alt="image.title"
                    class="w-full h-full object-cover"
                    @error="handleImageError"
                  />
                </div>
              </div>
            </div>
            
            <button 
              v-if="selectedProject.images.length > 1"
              @click="previousImage" 
              class="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
            >
              <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>
            
            <button 
              v-if="selectedProject.images.length > 1"
              @click="nextImage" 
              class="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
            >
              <svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
            
            <div v-if="selectedProject.images.length > 1" class="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
              <button 
                v-for="(image, index) in selectedProject.images" 
                :key="index"
                @click="goToImage(index)"
                :class="[
                  'w-2 h-2 rounded-full transition-all duration-200',
                  currentImageIndex === index ? 'bg-white' : 'bg-white bg-opacity-50'
                ]"
              ></button>
            </div>
          </div>
          
          <div class="space-y-1">
            <div>
              <span :class="[
                'px-3 py-1 rounded-full text-sm font-medium',
                getCategoryColor(selectedProject.category)
              ]">
                {{ selectedProject.category }}
              </span>
            </div>
            <p class="text-gray-600">{{ selectedProject.description }}</p>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span class="font-medium text-gray-900">Completed:</span>
                <span class="text-gray-600 ml-2">{{ selectedProject.date }}</span>
              </div>
            </div>
            <div class="pt-1">
              <h4 class="font-semibold text-gray-900 mb-0.5">Project Details:</h4>
              <p class="text-gray-600">{{ selectedProject.details }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Gallery',
  data() {
    return {
      activeFilter: 'all',
      selectedProject: null,
      currentImageIndex: 0,
      projects: [
        {
          id: 1,
          title: 'Luxury Vinyl Floor Installation',
          description: 'Complete Luxury Vinyl floor installation throughout the main living areas',
          category: 'flooring',
          date: 'March 2021',
          details: 'Installed Luxury Vinyl flooring throughout the living room, dining room, and hallway. The project included proper subfloor preparation, moisture barrier installation, and professional finishing.',
          images: [
            { title: 'Luxury Vinyl', url: 'https://i.postimg.cc/fbsNvKWQ/gray-vinyl1.jpg' },
            { title: 'Luxury Vinyl', url: 'https://i.postimg.cc/wjLzJPpk/gray-vinyl2.jpg' },
            { title: 'Luxury Vinyl', url: 'https://i.postimg.cc/k41PqPQ7/gray-vinyl5.jpg' },
            { title: 'Luxury Vinyl', url: 'https://i.postimg.cc/kMHPH9Mc/gray-viny4.jpg' },
            { title: 'Luxury Vinyl', url: 'https://i.postimg.cc/3JP7khPR/gray-vinyl3.jpg' },
            { title: 'Luxury Vinyl', url: 'https://i.postimg.cc/kGLrRNYZ/gray-vinyl9.jpg' },
            { title: 'Luxury Vinyl', url: 'https://i.postimg.cc/4dNRDxh8/gray-vinyl7.jpg' },
            { title: 'Luxury Vinyl', url: 'https://i.postimg.cc/tJsQgvkW/gray-vinyl6.jpg' },
          ]
        },
        {
          id: 2,
          title: 'Kitchen Remodel',
          description: 'Complete kitchen renovation with new cabinets and countertops',
          category: 'remodeling',
          date: 'February 2023',
          details: 'Full kitchen remodel including new cabinets, sparkling white quartz countertops and backsplash installation. The space was completely transformed for better functionality with high-end appliances.',
          images: [
            { title: 'Kitchen Remodel', url: 'https://i.postimg.cc/dt1WCT8Q/Modern-Kitchen-Champagne-Beach-floor1.jpg' },
            { title: 'Kitchen Remodel', url: 'https://i.postimg.cc/1XRvJ82H/Modern-Kitchen-Champagne-Beach-floor2.jpg' },
            { title: 'Kitchen Remodel', url: 'https://i.postimg.cc/tTNr33SJ/Modern-Kitchen-Champagne-Beach-floor3.jpg' },
            { title: 'Kitchen Remodel', url: 'https://i.postimg.cc/43t2dkpz/Modern-Kitchen-Champagne-Beach-floor4.jpg' },
            { title: 'Kitchen Remodel', url: 'https://i.postimg.cc/50V7Q7mD/Modern-Kitchen-Champagne-Beach-floor5.jpg' },
            { title: 'Kitchen Remodel', url: 'https://i.postimg.cc/nrd05Htt/Modern-Kitchen-Champagne-Beach-floor6.jpg' },
            { title: 'Kitchen Remodel', url: 'https://i.postimg.cc/WpSWt7qf/Modern-Kitchen-Champagne-Beach-floor7.jpg' },
          ]
        },
        {
          id: 3,
          title: 'Drywall Repair & Texturing',
          description: 'Repaired damaged drywall and installed Marble Ledger Pane ',
          category: 'drywall',
          date: 'January 2025',
          details: 'Repaired multiple areas of damaged drywall, including holes and water damage. The 6 x 24 marble ledger features beige hues with a textured finish. Install it on a wall to create a focal point, or use it to elevate the look of your design.',
          images: [
            { title: 'Marble Ledger Pane', url: 'https://i.postimg.cc/qBPyrKzN/marble-wall1.jpg' },
            { title: 'Marble Ledger Pane', url: 'https://i.postimg.cc/9FyTQf7J/marble-wall2.jpg' },
            { title: 'Marble Ledger Pane', url: 'https://i.postimg.cc/3Ry05rhj/marble-wall3.jpg' },
            { title: 'Marble Ledger Pane', url: 'https://i.postimg.cc/FFd3wpst/marble-wall4.jpg' },
            { title: 'Marble Ledger Pane', url: 'https://i.postimg.cc/s1CVLsf2/marble-wall5.jpg' },
            { title: 'Marble Ledger Pane', url: 'https://i.postimg.cc/BnsFLG5s/marble-wall6.jpg' },
            { title: 'Marble Ledger Pane', url: 'https://i.postimg.cc/hGZQ5zkP/marble-wall7.jpg' },
            { title: 'Marble Ledger Pane', url: 'https://i.postimg.cc/yN8SjXrb/marble-wall8.jpg' },
            { title: 'Marble Ledger Pane', url: 'https://i.postimg.cc/3xBv2b9G/marble-wall9.jpg' },
          ]
        },
        {
          id: 4,
          title: 'Custom Wall Molding',
          description: 'Installed custom wall molding',
          category: 'trimming',
          date: 'December 2023',
          details: 'Designed and installed real wood molding. The wall molding adds elegance and architectural interest to the living area and well as a modern touch to the bedroom.',
          images: [
            { title: 'Wall Molding', url: 'https://i.postimg.cc/1zFkn7kZ/wall-molding1.jpg' },
            { title: 'Wall Molding', url: 'https://i.postimg.cc/L8GdsF0T/wall-molding2.jpg' },
            { title: 'Wall Molding', url: 'https://i.postimg.cc/x86hDwzL/wall-molding4.jpg' },
            { title: 'Wall Molding', url: 'https://i.postimg.cc/cCx2zxrs/wall-molding3.jpg' },
            { title: 'Wall Molding', url: 'https://i.postimg.cc/pTY3khVG/wall-molding5.jpg' },
            { title: 'Wall Molding', url: 'https://i.postimg.cc/bJjKQHPS/wall-molding6.jpg' },
          ]
        },
        {
          id: 5,
          title: 'Cabinet painting',
          description: 'Complete cabinet kitchen painting with backsplash installation',
          category: 'painting',
          date: 'November 2020',
          details: 'Professional kitchen cabinet painting service including color consultation, surface preparation, and high-quality paint application. Installed hexagon marble backsplash in the kitchen.',
          images: [
            { title: 'cabinet kitchen painting', url: 'https://i.postimg.cc/T17v9M7K/Cabinet-paint1.jpg' },
            { title: 'cabinet kitchen painting', url: 'https://i.postimg.cc/gkjW0jj8/Cabinet-paint2.jpg' },
            { title: 'cabinet kitchen painting', url: 'https://i.postimg.cc/TPMMQc9J/Cabinet-paint3.jpg' },
            { title: 'cabinet kitchen painting', url: 'https://i.postimg.cc/QMQLzWYG/Cabinet-paint4.jpg' },
            { title: 'cabinet kitchen painting', url: 'https://i.postimg.cc/XYy6wPwv/Cabinet-paint5.jpg' },
            { title: 'cabinet kitchen painting', url: 'https://i.postimg.cc/4dXCRNjK/Cabinet-paint6.jpg' },
            { title: 'cabinet kitchen painting', url: 'https://i.postimg.cc/3NJHmJBM/Cabinet-paint7.jpg' },
            { title: 'cabinet kitchen painting', url: 'https://i.postimg.cc/BZM3sHSJ/Cabinet-paint8.jpg' },
          ]
        },
        {
          id: 6,
          title: 'Porcelain Installation',
          description: 'Installed Alpe Graphite Matte porcelain', 
          category: 'flooring',
          date: 'October 2023',
          details: 'Installed high-quality porcelain tile flooring in the outside area of the house. The project included removing the old tile flooring and installing the new one.',
          images: [
            { title: 'Gray Floor', url: 'https://i.postimg.cc/zfNshF8b/gray-tile10.jpg' },
            { title: 'Gray Floor', url: 'https://i.postimg.cc/kX03JdHy/gray-tile1.jpg' },
            { title: 'Gray Floor', url: 'https://i.postimg.cc/76YjsPCq/gray-tile3.jpg' },
            { title: 'Gray Floor', url: 'https://i.postimg.cc/3wZPF0xL/gray-tile2.jpg' },
            { title: 'Gray Floor', url: 'https://i.postimg.cc/VLrPPNK6/gray-tile4.jpg' },
            { title: 'Gray Floor', url: 'https://i.postimg.cc/T2HvsbqF/gray-tile5.jpg' },
            { title: 'Gray Floor', url: 'https://i.postimg.cc/L8jdhbKF/gray-tile6.jpg' },
            { title: 'Gray Floor', url: 'https://i.postimg.cc/wM38vxHw/gray-tile7.jpg' },
            { title: 'Gray Floor', url: 'https://i.postimg.cc/63Mxt4s7/gray-tile8.jpg' },
            { title: 'Gray Floor', url: 'https://i.postimg.cc/Y9rB5fPK/gray-tile9.jpg' },
          ]
        },
        {
          id: 7,
          title: 'Baseboard Installation and Sliding Door Trim',
          description: 'New baseboard and Sliding Door Trim throughout the home',
          category: 'trimming',
          date: 'September 2023',
          details: 'Installed new baseboards throughout the home with proper miter cuts and caulking. The baseboards provide a finished look and protect walls from damage. The trim work enhances the architectural detail and provides a polished finish.',
          images: [
            { title: 'Baseboards', url: 'https://i.postimg.cc/m2HMQR2q/basebord-and-casing1.jpg' },
            { title: 'Baseboards', url: 'https://i.postimg.cc/NMCTxjXh/basebord-and-casing2.jpg' },
            { title: 'Baseboards', url: 'https://i.postimg.cc/VsTnr51n/basebord-and-casing3.jpg' },
            { title: 'Baseboards', url: 'https://i.postimg.cc/8cFvXgd7/basebord-and-casing4.jpg' },
            { title: 'Baseboards', url: 'https://i.postimg.cc/4yr9r2wB/basebord-and-casing5.jpg' },
            { title: 'Baseboards', url: 'https://i.postimg.cc/9QP7np9Y/basebord-and-casing6.jpg' },
            { title: 'Baseboards', url: 'https://i.postimg.cc/W1cZ8Kd4/basebord-and-casing7.jpg' },
          ]
        },
        {
          id: 8,
          title: 'Drywall Repair & Interior Painting',
          description: 'Complete drywall repair and interior painting service',
          category: 'drywall',
          date: 'August 2023',
          details: 'Comprehensive drywall repair and interior painting project including patching holes, repairing cracks, sanding surfaces, and applying high-quality interior paint. The project involved proper surface preparation, caulking, and professional paint application to transform the space with a fresh, modern finish.',
          images: [
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/c4r0v4Tq/Paint-job5.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/28MmhRX1/Paint-job1.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/SRt4gwSt/Paint-job2.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/FFPhFJyg/Paint-job3.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/FHSvxW2D/Paint-job4.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/MGb8CcZB/Paint-job6.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/8CVSXdGD/Paint-job7.jpg' }
          ]
        },
        {
          id: 9,
          title: 'Bathroom Remodel',
          description: 'Full bathroom renovation with new fixtures.',
          category: 'remodeling',
          date: 'July 2023',
          details: 'Complete bathroom remodel including new sinks, counter top installation, custom vanity, and smart mirror. Transformed an outdated bathroom into a modern retreat.',
          images: [
            { title: 'new bathroom', url: 'https://i.postimg.cc/pTMNt9Wv/modern-bath.jpg' },
            { title: 'new bathroom', url: 'https://i.postimg.cc/wvXCx6Mm/modern-bath1.jpg' },
            { title: 'new bathroom', url: 'https://i.postimg.cc/WzsQhQqf/modern-bath2.jpg' },
            { title: 'new bathroom', url: 'https://i.postimg.cc/MHtgRdyZ/modern-bath4.jpg' },
            { title: 'new bathroom', url: 'https://i.postimg.cc/Nff3YvcK/modern-bath5.jpg' },
            { title: 'new bathroom', url: 'https://i.postimg.cc/wxkCSTSK/modern-bath6.jpg' },
            { title: 'new bathroom', url: 'https://i.postimg.cc/8Ctv2vBJ/modern-bath7.jpg' }
          ]
        },
        {
          id: 10,
          title: 'Popcorn Ceiling Removal',
          description: 'Complete popcorn ceiling removal and smooth finish application',
          category: 'drywall',
          date: 'January 2025',
          details: 'Professional popcorn ceiling removal service including proper surface preparation, scraping, sanding, and application of smooth finish. The process involved careful removal of the textured popcorn material, repair of any damaged areas, and application of a smooth ceiling finish for a modern, clean appearance.',
          images: [
            { title: 'Popcorn Ceiling', url: 'https://i.postimg.cc/mkN4styJ/popcor1.jpg' },
            { title: 'Popcorn Ceiling', url: 'https://i.postimg.cc/5yMJTwL8/popcorn2.jpg' },
            { title: 'Popcorn Ceiling', url: 'https://i.postimg.cc/rFjTZRmt/popcorn3.jpg' },
            { title: 'Popcorn Ceiling', url: 'https://i.postimg.cc/zGfrsDb2/popcorn4.jpg' },
            { title: 'Popcorn Ceiling', url: 'https://i.postimg.cc/XvG0v2gw/popcorn5.jpg' },
            { title: 'Popcorn Ceiling', url: 'https://i.postimg.cc/GmSCd0vL/popcorn6.jpg' },
            { title: 'Popcorn Ceiling', url: 'https://i.postimg.cc/59wbvRWg/popcorn7.jpg' },
            { title: 'Popcorn Ceiling', url: 'https://i.postimg.cc/NfBYxkgc/popcorn8.jpg' },
          ]
        },
        {
          id: 12,
          title: 'Vinyl Floor Installation',
          description: 'Luxury vinyl plank flooring complete house',
          category: 'flooring',
          date: 'April 2020',
          details: 'Installed luxury vinyl plank flooring in a 1254 sq. ft house. The waterproof flooring provides durability making it ideal for moisture-prone areas, resistance to scratches and stains, and ease of maintenance.',
          images: [
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/85Y23QQ9/brown-floor.jpg' },
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/8zDzwWbW/brown-floor1.jpg' },
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/dQk30J17/brown-floor3.jpg' },
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/bvKYMNSc/brown-floor10.jpg' },
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/SR4SSTGg/brown-floor11.jpg' },
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/jS3SHhsj/brown-floor6.jpg' },
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/W4DbXJ3w/brown-floor5.jpg' },
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/nr1HC4Rp/brown-floor4.jpg' },
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/SsCQpFcX/brown-floor12.jpg' },
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/3J2wg92Y/brown-floor9.png' },
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/0Ny21sFy/brown-floor8.png' },
            { title: 'Vinyl Flooring', url: 'https://i.postimg.cc/BngQz6hz/brown-floor7.png' },
          ]
        },
        {
          id: 13,
          title: 'Business Remodel',
          description: '',
          category: 'remodeling',
          date: 'July 2019',
          details: '.',
          images: [
            { title: '', url: 'https://i.postimg.cc/XqZDG47L/business4.jpg' },
            { title: '', url: 'https://i.postimg.cc/wTmG9qrT/business1.jpg' },
            { title: '', url: 'https://i.postimg.cc/WpKyRFXH/business2.jpg' },
            { title: '', url: 'https://i.postimg.cc/8zgnc88d/business3.jpg' },
            { title: '', url: 'https://i.postimg.cc/DZ3qdhg5/business5.jpg' },
            { title: '', url: 'https://i.postimg.cc/1t0Dvd7S/business6.jpg' },

          ]
        },
        {
          id: 14,
          title: 'Porcelain Installation',
          description: 'Shadow Wood tile installation in garage',
          category: 'flooring',
          date: 'April 2020',
          details: 'Installed Porcelain in the garage. The porcelain tile is a durable and low-maintenance flooring option that is easy to clean and maintain. A laundry room was built in the garage to provide a convenient space for laundry and storage.',
          images: [
            { title: 'Shadow Floor', url: 'https://i.postimg.cc/Gp3SCkYv/Shadow-Floor.jpg' },
            { title: 'Shadow Floor', url: 'https://i.postimg.cc/Z5xD2f9T/Shadow-Floor2.jpg' },
            { title: 'Shadow Floor', url: 'https://i.postimg.cc/XNKHpH89/Shadow-Floor3.jpg' },
            { title: 'Shadow Floor', url: 'https://i.postimg.cc/fWYFYTwt/Shadow-Floor4.jpg' }, 
          ]
        },
        {
          id: 15,
          title: 'Interior Painting',
          description: 'Interior painting service',
          category: 'painting',
          date: 'August 2023',
          details: 'Comprehensive drywall repair and interior painting project including patching holes, repairing cracks, sanding surfaces, and applying high-quality interior paint. The project involved proper surface preparation, caulking, and professional paint application to transform the space with a fresh, modern finish.',
          images: [
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/28MmhRX1/Paint-job1.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/SRt4gwSt/Paint-job2.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/FFPhFJyg/Paint-job3.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/FHSvxW2D/Paint-job4.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/c4r0v4Tq/Paint-job5.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/MGb8CcZB/Paint-job6.jpg' },
            { title: 'Drywall & Paint', url: 'https://i.postimg.cc/8CVSXdGD/Paint-job7.jpg' }
          ]
        },
      ]
    }
  },
  computed: {
    filteredProjects() {
      if (this.activeFilter === 'all') {
        return this.projects
      }
      return this.projects.filter(project => project.category === this.activeFilter)
    }
  },
  methods: {
    openModal(project) {
      this.selectedProject = project
      this.currentImageIndex = 0
    },
    closeModal() {
      this.selectedProject = null
    },
    getCategoryColor(category) {
      const colors = {
        flooring: 'bg-amber-100 text-amber-800',
        drywall: 'bg-gray-100 text-gray-800',
        trimming: 'bg-orange-100 text-orange-800',
        painting: 'bg-purple-100 text-purple-800',
        remodeling: 'bg-green-100 text-green-800'
      }
      return colors[category] || 'bg-gray-100 text-gray-800'
    },
    previousImage() {
      this.currentImageIndex = (this.currentImageIndex - 1 + this.selectedProject.images.length) % this.selectedProject.images.length
    },
    nextImage() {
      this.currentImageIndex = (this.currentImageIndex + 1) % this.selectedProject.images.length
    },
    goToImage(index) {
      this.currentImageIndex = index
    },
    handleImageError(event) {
      event.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Available'
    }
  }
}
</script> 